/**
 * Request Cache and Deduplication System
 * Prevents duplicate API calls and caches results for better performance
 */

// Cache storage
const cache = new Map();
const pendingRequests = new Map();

// Cache TTL (time to live) for different types of operations
const CACHE_TTL = {
  ownership: 5 * 60 * 1000, // 5 minutes for ownership checks
  userSettings: 10 * 60 * 1000, // 10 minutes for user settings
  spaceData: 2 * 60 * 1000, // 2 minutes for space data
  unpublishedCount: 30 * 1000, // 30 seconds for unpublished counts
  default: 60 * 1000, // 1 minute default
};

/**
 * Creates a cache key from function name and parameters
 * @param {string} type - Type of operation (ownership, userSettings, etc.)
 * @param {string} key - Unique identifier for the request
 * @returns {string} Cache key
 */
function createCacheKey(type, key) {
  return `${type}:${key}`;
}

/**
 * Checks if a cached value is still valid
 * @param {Object} cacheEntry - Cached entry with timestamp
 * @param {number} ttl - Time to live in milliseconds
 * @returns {boolean} Whether the cache entry is valid
 */
function isCacheValid(cacheEntry, ttl) {
  if (!cacheEntry) return false;
  return Date.now() - cacheEntry.timestamp < ttl;
}

/**
 * Cached ownership check - prevents duplicate RPC calls
 * @param {Object} supabaseClient - Supabase client
 * @param {string} spaceSlug - Space slug to check ownership for
 * @param {string} userId - User ID for additional cache segmentation
 * @returns {Promise<boolean>} Ownership status
 */
export async function getCachedOwnership(supabaseClient, spaceSlug, userId) {
  const cacheKey = createCacheKey("ownership", `${spaceSlug}:${userId}`);
  const ttl = CACHE_TTL.ownership;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (isCacheValid(cached, ttl)) {
    console.log(`🎯 Cache hit for ownership: ${spaceSlug}`);
    return cached.data;
  }

  // Check if request is already pending
  if (pendingRequests.has(cacheKey)) {
    console.log(`⏳ Awaiting pending ownership request: ${spaceSlug}`);
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  console.log(`🔍 Making new ownership request: ${spaceSlug}`);
  const { lightweightRPC } = await import("./timeout-handler");

  const requestPromise = (async () => {
    try {
      const { data, error } = await lightweightRPC(
        supabaseClient,
        "is_space_owner",
        { space_slug: spaceSlug },
        15000,
        false // Silent
      );

      const result = error ? null : data === true;

      // Cache the result
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch (err) {
      // Don't cache errors, but don't throw either
      return null;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    }
  })();

  // Store pending request
  pendingRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Cached user settings fetch
 * @param {Object} supabaseClient - Supabase client
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User settings
 */
export async function getCachedUserSettings(supabaseClient, userId) {
  const cacheKey = createCacheKey("userSettings", userId);
  const ttl = CACHE_TTL.userSettings;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (isCacheValid(cached, ttl)) {
    console.log(`🎯 Cache hit for user settings: ${userId.substring(0, 8)}...`);
    return cached.data;
  }

  // Check if request is already pending
  if (pendingRequests.has(cacheKey)) {
    console.log(
      `⏳ Awaiting pending user settings request: ${userId.substring(0, 8)}...`
    );
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  console.log(
    `🔍 Making new user settings request: ${userId.substring(0, 8)}...`
  );

  const requestPromise = (async () => {
    try {
      const { data, error } = await supabaseClient
        .from("user_settings")
        .select("*")
        .eq("id", userId)
        .single();

      const result = error ? null : data;

      // Cache the result
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch (err) {
      return null;
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    }
  })();

  // Store pending request
  pendingRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Cached space data fetch
 * @param {string} slug - Space slug
 * @param {Function} fetchFunction - Function to fetch space data
 * @returns {Promise<Object>} Space data
 */
export async function getCachedSpaceData(slug, fetchFunction) {
  const cacheKey = createCacheKey("spaceData", slug);
  const ttl = CACHE_TTL.spaceData;

  // Check cache first
  const cached = cache.get(cacheKey);
  if (isCacheValid(cached, ttl)) {
    console.log(`🎯 Cache hit for space data: ${slug}`);
    return cached.data;
  }

  // Check if request is already pending
  if (pendingRequests.has(cacheKey)) {
    console.log(`⏳ Awaiting pending space data request: ${slug}`);
    return pendingRequests.get(cacheKey);
  }

  // Make new request
  console.log(`🔍 Making new space data request: ${slug}`);

  const requestPromise = (async () => {
    try {
      const result = await fetchFunction();

      // Cache the result
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      return result;
    } catch (err) {
      throw err; // Let the caller handle errors
    } finally {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    }
  })();

  // Store pending request
  pendingRequests.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Invalidates cache entries for a specific type or key
 * @param {string} pattern - Pattern to match (e.g., 'ownership:*' or specific key)
 */
export function invalidateCache(pattern) {
  const keys = Array.from(cache.keys());
  const matchingKeys = keys.filter((key) =>
    pattern.endsWith("*")
      ? key.startsWith(pattern.slice(0, -1))
      : key === pattern
  );

  matchingKeys.forEach((key) => {
    cache.delete(key);
    console.log(`🗑️ Invalidated cache: ${key}`);
  });
}

/**
 * Invalidates user settings cache for a specific user
 * @param {string} userId - User ID to invalidate cache for
 */
export function invalidateUserSettingsCache(userId) {
  const userSettingsKey = createCacheKey("userSettings", userId);
  if (cache.has(userSettingsKey)) {
    cache.delete(userSettingsKey);
    console.log(
      `🗑️ Invalidated user settings cache for: ${userId.substring(0, 8)}...`
    );
  }
}

/**
 * Clears expired cache entries
 */
export function cleanupCache() {
  const now = Date.now();
  const keys = Array.from(cache.keys());

  keys.forEach((key) => {
    const entry = cache.get(key);
    const type = key.split(":")[0];
    const ttl = CACHE_TTL[type] || CACHE_TTL.default;

    if (!isCacheValid(entry, ttl)) {
      cache.delete(key);
    }
  });
}

/**
 * Gets cache statistics for debugging
 * @returns {Object} Cache statistics
 */
export function getCacheStats() {
  return {
    cacheSize: cache.size,
    pendingRequests: pendingRequests.size,
    cacheKeys: Array.from(cache.keys()),
    pendingKeys: Array.from(pendingRequests.keys()),
  };
}

// Cleanup expired entries every 5 minutes
if (typeof window !== "undefined") {
  setInterval(cleanupCache, 5 * 60 * 1000);
}

// Export cache management functions
export { cache, pendingRequests, CACHE_TTL };
